import type {
  DataFilterField, DataFilterOption,
} from '../../../shared/ui/data-filter/data-filter.component';
import { businessTypeLabel } from './business-types';
import { LEAD_SOURCE_OPTIONS } from './lead-list-table';
import type { AssignableRep, LeadStage } from './leads-board.component';

/**
 * The two closed vocabularies the Leads filters offer that are not loaded from
 * the server. Their values are the ones the database stores; the labels are
 * the page's wording for them.
 */
const LEAD_ORIGINS: readonly DataFilterOption[] = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'import', label: 'Import' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'firstparty', label: 'First party' },
  { value: 'manual', label: 'Manual' },
];

const ROUTE_FIT_STATUSES: readonly DataFilterOption[] = [
  { value: 'Feasible', label: 'Feasible' },
  { value: 'Infeasible', label: 'Infeasible' },
  { value: 'NeedsReview', label: 'Needs review' },
  { value: 'Unknown', label: 'Unknown' },
];

/**
 * The columns a lead can be narrowed by, for the shared filter bar - on the
 * Leads page, and under a patch on the Territories page.
 *
 * Lifted out of the Leads board for the reason LEAD_LIST_COLUMNS was: the
 * Territories page carries the same table of leads, and a second list of its
 * filters written beside the first is how one of them stops matching the
 * server. The ids are the server's whitelist (WorkItemFilterFields), and a
 * contract test holds the two together.
 *
 * A function because three columns' options arrive late - the stages, the
 * business types and the roster are each read from the server, and a bar
 * built before they land would offer a dropdown with nothing in it.
 */
export function leadRuleFilterFields(
  businessTypes: readonly { name: string }[],
  stages: readonly Pick<LeadStage, 'code' | 'name'>[],
  reps: readonly AssignableRep[],
): DataFilterField[] {
  const none: readonly DataFilterOption[] = [];
  return [
    // Text: the columns someone half-remembers rather than picks from a list.
    { id: 'title', label: 'Title', kind: 'text', pinned: false, options: none },
    { id: 'address', label: 'Address', kind: 'text', pinned: false, options: none },
    // The table's own three location columns, by the table's own labels.
    { id: 'city', label: 'City', kind: 'text', pinned: false, options: none },
    { id: 'state', label: 'State', kind: 'text', pinned: false, options: none },
    { id: 'postalCode', label: 'ZIP', kind: 'text', pinned: false, options: none },
    // The type half of the Next action cell. Typed on the lead, not picked,
    // so it is text; the due half is the date rule further down.
    {
      id: 'nextActionType', label: 'Next action', kind: 'text', pinned: false,
      options: none,
    },

    // Choice: a closed set, so the dropdown can list it and nobody has to
    // know how a value is spelled in the database.
    {
      // The kinds the Sources cell prints, worded the way it words them.
      // How many links merged is still `sourceCount`, below.
      id: 'sources', label: 'Source', pinned: false, allLabel: 'Any source',
      options: LEAD_SOURCE_OPTIONS,
    },
    {
      id: 'businessTypes', label: 'Types', pinned: false,
      allLabel: 'Any type',
      // Value is the store_type name, which is what the API filters on;
      // the label is the same name written for a person to read.
      options: businessTypes.map(type => ({
        value: type.name, label: businessTypeLabel(type.name),
      })),
    },
    {
      id: 'stage', label: 'Stage', pinned: false, allLabel: 'Any stage',
      options: stages.map(stage => ({
        value: stage.code, label: stage.name,
      })),
    },
    {
      id: 'origin', label: 'Origin', pinned: false, allLabel: 'Any origin',
      options: LEAD_ORIGINS,
    },
    {
      id: 'salesRep', label: 'Sales rep', pinned: false, allLabel: 'Any sales rep',
      // Named the way the Leads board names an owner.
      options: reps.map(rep => ({
        value: rep.userId,
        label: rep.displayName?.trim() || rep.userName?.trim() || `Owner ${rep.userId}`,
      })),
    },
    {
      id: 'routeFit', label: 'Route fit', pinned: false,
      allLabel: 'Any route fit', options: ROUTE_FIT_STATUSES,
    },

    // Number and date carry a comparison, because one value is not a
    // question on a column that is ordered.
    {
      id: 'sourceCount', label: 'Sources merged', kind: 'number', pinned: false,
      options: none,
    },
    { id: 'score', label: 'Score', kind: 'number', pinned: false, options: none },
    {
      id: 'contribution', label: 'Contribution', kind: 'number', pinned: false,
      options: none,
    },
    {
      id: 'probability', label: 'Probability', kind: 'number', pinned: false,
      options: none,
    },
    {
      id: 'nextActionDue', label: 'Next action due', kind: 'date', pinned: false,
      options: none,
    },
    {
      id: 'discoveredAt', label: 'Discovered', kind: 'date', pinned: false,
      options: none,
    },
  ];
}
